import { useEffect } from "react";

const YOGO_SCRIPT_SRC = "https://luftpilates.yogobooking.pt/widgets-v2/yogo-widgets.js";
const YOGO_SCRIPT_ID = "yogo-widgets-loader";

/**
 * Ensures the official YOGO widgets loader is present on the page and
 * re-hydrates any `.yogo-calendar` mount that ends up empty (e.g. after
 * SPA route change or CDN/browser caching the pre-YOGO index.html).
 *
 * Does NOT recreate or simulate the calendar — it only helps YOGO's own
 * script find the mount elements.
 *
 * Idempotent: safe to call from multiple pages / re-mounts.
 */
export function useYogoWidget() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const ensureScript = () =>
      new Promise((resolve, reject) => {
        // Already injected & loaded
        if (window.YOGO_WIDGETS_V2_LOADED) return resolve("loaded");

        const existing = document.getElementById(YOGO_SCRIPT_ID)
          || document.querySelector(`script[src="${YOGO_SCRIPT_SRC}"]`);
        if (existing) {
          existing.addEventListener("load", () => resolve("existing-loaded"), { once: true });
          existing.addEventListener("error", reject, { once: true });
          // If it was already parsed before we listened, poll briefly for the loaded flag
          const poll = setInterval(() => {
            if (window.YOGO_WIDGETS_V2_LOADED) {
              clearInterval(poll);
              resolve("existing-poll");
            }
          }, 200);
          setTimeout(() => clearInterval(poll), 8000);
          return;
        }

        const s = document.createElement("script");
        s.id = YOGO_SCRIPT_ID;
        s.src = YOGO_SCRIPT_SRC;
        s.async = true;
        s.onload = () => resolve("injected");
        s.onerror = reject;
        document.head.appendChild(s);
      });

    let cancelled = false;

    (async () => {
      try {
        await ensureScript();
      } catch (err) {
        // If the script itself fails (network/CSP), we can't recover in JS —
        // surface a clear console error so it shows up in DevTools during testing.
        // eslint-disable-next-line no-console
        console.error("[YOGO] widget loader failed to load", err);
        return;
      }

      // The loader typically hydrates on document-ready + observes DOM.
      // If our mount is still empty after ~2s (SPA navigation edge case),
      // "kick" it by cloning the empty div — this re-triggers the loader's
      // MutationObserver without touching YOGO internals.
      const kick = () => {
        if (cancelled) return;
        const el = document.querySelector(".yogo-calendar");
        if (!el) return;
        if (el.childElementCount > 0) return; // already hydrated

        // Clone attributes into a fresh node so any observer sees a new node
        const fresh = document.createElement("div");
        for (const attr of el.attributes) fresh.setAttribute(attr.name, attr.value);
        el.replaceWith(fresh);
      };

      // Two chances: quick and slower fallback
      const t1 = window.setTimeout(kick, 1500);
      const t2 = window.setTimeout(kick, 4000);

      return () => {
        cancelled = true;
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    })();

    return () => {
      cancelled = true;
    };
  }, []);
}
